/* eslint-disable @typescript-eslint/no-unused-vars */
// src/pages/JoinTripPage.tsx
// Page for accepting group trip invitations

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  Stack,
  Divider,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { parseLocalDate } from '../utils/dateHelpers';
import { isAuthenticated, validateSession } from '../utils/api';
import * as groupTripsApi from '../services/groupTripsApi';
import type { InviteDetails } from '../types/groupTrips';

export default function JoinTripPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<any>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated()) {
        const session = await validateSession();
        if (session.valid && session.user) {
          setUser(session.user);
        }
      }
      setAuthChecked(true);
    };
    checkAuth();
  }, []);

  // Fetch invite details
  useEffect(() => {
    const fetchInvite = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }
      
      try {
        const data = await groupTripsApi.getInviteDetails(token);
        setInvite(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Invalid or expired invitation');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInvite();
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    
    if (!isAuthenticated()) {
      // Store the return URL and redirect to login
      sessionStorage.setItem('returnUrl', `/trips/join/${token}`);
      navigate('/login');
      return;
    }
    
    setJoining(true);
    setError(null);
    
    try {
      const result = await groupTripsApi.acceptInvite(token);
      setJoined(true);
      
      // Redirect to trip after short delay
      setTimeout(() => {
        navigate(`/trips/${result.tripId}`);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join trip');
      setJoining(false);
    }
  };

  const handleDecline = async () => {
    if (!token) return;
    
    try {
      await groupTripsApi.declineInvite(token);
      navigate('/my-trips');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to decline invitation');
    }
  };

  // Loading state
  if (loading || !authChecked) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress sx={{ color: '#2D6E6A' }} />
      </Box>
    );
  }

  // Success state
  if (joined) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Card sx={{ maxWidth: 400, textAlign: 'center' }}>
          <CardContent sx={{ p: 4 }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              You're In!
            </Typography>
            <Typography color="text.secondary">
              Redirecting you to the trip...
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Error state (no valid invite)
  if (error && !invite) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Card sx={{ maxWidth: 400 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CancelIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Invalid Invitation
            </Typography>
            <Typography color="text.secondary" paragraph>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' } }}
            >
              Go Home
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Main invite display
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#2D6E6A' }}>
              You're Invited!
            </Typography>
            
            <Typography variant="h5" gutterBottom>
              {invite?.tripTitle}
            </Typography>
            
            <Stack spacing={2} sx={{ my: 3 }}>
              {invite?.destination && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon color="action" />
                  <Typography>{invite.destination}</Typography>
                </Box>
              )}
              
              {invite?.startDate && invite?.endDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon color="action" />
                  <Typography>
                    {format(parseLocalDate(invite.startDate)!, 'MMM d')} - {format(parseLocalDate(invite.endDate)!, 'MMM d, yyyy')}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon color="action" />
                <Typography>
                  {invite?.memberCount} {invite?.memberCount === 1 ? 'member' : 'members'} already joined
                </Typography>
              </Box>
            </Stack>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ bgcolor: '#2D6E6A' }}>
                {invite?.organizerName?.[0] || 'O'}
              </Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Invited by
                </Typography>
                <Typography variant="subtitle1">
                  {invite?.organizerName}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                You'll be joining as
              </Typography>
              <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                {invite?.role || 'member'}
              </Typography>
            </Box>
            
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            {!isAuthenticated() && (
              <Alert severity="info" sx={{ mb: 3 }}>
                You'll need to sign in or create an account to join this trip.
              </Alert>
            )}
            
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleAccept}
                disabled={joining}
                startIcon={joining ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' } }}
              >
                {isAuthenticated() ? (joining ? 'Joining...' : 'Accept & Join') : 'Sign In to Join'}
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={handleDecline}
                disabled={joining}
                startIcon={<CancelIcon />}
              >
                Decline
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
